// Return-to-revision modal: textarea + preset reasons. Confirms by issuing
// the `return` transition; the reason is stored on the request and surfaced
// in the manager's return banner.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Eyebrow, Icon, Modal, Textarea } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { useTransition } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import type { RequestView, UserRef } from '../../types.js';

const PRESET_KEYS = [
  'returnModal.presets.missingDescription',
  'returnModal.presets.weightVolume',
] as const;

export interface ReturnRequestModalProps {
  request: RequestView;
  me: UserRef;
}

export function ReturnRequestModal({ request }: ReturnRequestModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [reason, setReason] = useState('');
  const [preset, setPreset] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const setModal = useRequestsStore((s) => s.setModal);
  const transitionMutation = useTransition(request.id);

  const onClose = () => setModal(null);

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    try {
      await transitionMutation.mutateAsync({ key: 'return', body: { reason: reason.trim() } });
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:returnModal.title', { id: request.id })}
      width={520}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="danger"
            icon="rotate-ccw"
            disabled={!reason.trim() || transitionMutation.isPending}
            onClick={() => {
              void onSubmit();
            }}
          >
            {t('requests:returnModal.submit')}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 12 }}>
        {t('requests:returnModal.explanation')}
      </div>
      <div style={{ marginBottom: 8 }}>
        <Eyebrow>{t('requests:returnModal.presetsTitle')}</Eyebrow>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {PRESET_KEYS.map((key) => {
            const p = t(`requests:${key}`);
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setPreset(p);
                  setReason(p);
                }}
                style={{
                  fontSize: 12,
                  padding: '5px 10px',
                  borderRadius: 'var(--r-1)',
                  border: `1px solid ${preset === p ? 'var(--accent-1)' : 'var(--border-1)'}`,
                  background: preset === p ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
                  color: preset === p ? 'var(--accent-1-press)' : 'var(--fg-2)',
                  cursor: 'pointer',
                }}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 5 }}>
          {t('requests:returnModal.reasonLabel')}{' '}
          <span style={{ color: 'var(--status-error-fg)' }}>*</span>
        </div>
        <Textarea
          value={reason}
          onChange={(v) => {
            setReason(v);
            if (v !== preset) setPreset(null);
          }}
          rows={4}
          placeholder={t('requests:returnModal.reasonPlaceholder')}
        />
      </div>
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}
